import { InputAdornment, TextField } from '@mui/material'
import type { EventSettings } from '../api/generated/EventSettings'
import { PhoneField } from '../phone/PhoneField'
import { EventDateFields } from './EventDateFields'

/** Settings edited in a form; registrations are opened and closed separately. */
export type SettingsForm = Omit<EventSettings, 'applications_open'>

const timezones = Intl.supportedValuesOf('timeZone')

export function defaultSettings(): SettingsForm {
  return {
    name: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    contact_phone: null,
    reminder_lead_minutes: 15,
    withdrawal_deadline_hours: 48,
    starts_on: null,
    ends_on: null,
  }
}

const inputValue = (e: Event) => (e.target as HTMLInputElement).value

export function EventSettingsFields({
  value,
  datesRequired,
  onChange,
}: {
  value: SettingsForm
  datesRequired?: boolean
  onChange: (value: SettingsForm) => void
}) {
  // The browser list may lack a valid zone saved elsewhere (alias, UTC)
  const options = timezones.includes(value.timezone) ? timezones : [value.timezone, ...timezones]
  return (
    <>
      <TextField
        label="Nom de l'événement"
        required
        slotProps={{ htmlInput: { maxLength: 200 } }}
        value={value.name}
        onInput={(e) => onChange({ ...value, name: inputValue(e) })}
      />
      <EventDateFields
        value={value}
        required={datesRequired}
        onChange={(dates) => onChange({ ...value, ...dates })}
      />
      <TextField
        label="Fuseau horaire"
        select
        required
        slotProps={{ select: { native: true } }}
        value={value.timezone}
        onChange={(e) => onChange({ ...value, timezone: inputValue(e) })}
      >
        {options.map((timezone) => (
          <option key={timezone} value={timezone}>
            {timezone}
          </option>
        ))}
      </TextField>
      <PhoneField
        label="Téléphone de contact"
        autoComplete="tel"
        helperText="Affiché aux bénévoles"
        slotProps={{ htmlInput: { pattern: '\\+?[0-9 ]{6,}' } }}
        value={value.contact_phone ?? ''}
        onValue={(phone) => onChange({ ...value, contact_phone: phone || null })}
      />
      <TextField
        label="Rappel avant la prise de poste"
        type="number"
        required
        slotProps={{
          htmlInput: { min: 1, max: 1440 },
          input: { endAdornment: <InputAdornment position="end">min</InputAdornment> },
        }}
        value={value.reminder_lead_minutes}
        onInput={(e) => onChange({ ...value, reminder_lead_minutes: Number(inputValue(e)) })}
      />
      <TextField
        label="Désistement possible jusqu'à"
        type="number"
        required
        helperText="Avant le créneau. Ensuite, le bénévole écrit aux organisateurs"
        slotProps={{
          htmlInput: { min: 0, max: 720 },
          input: { endAdornment: <InputAdornment position="end">h avant</InputAdornment> },
        }}
        value={value.withdrawal_deadline_hours}
        onInput={(e) => onChange({ ...value, withdrawal_deadline_hours: Number(inputValue(e)) })}
      />
    </>
  )
}
